import { Grid } from '@mui/material';
import { CrossIcon, HSpacer, RoutalDialog } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import moment from 'moment-timezone';
import Back from '../../../../assets/img/back-arrow.svg';
import { Vehicle } from '../../../../domain/vehicle/Vehicle';
import { useRetrieveOrganization } from '../../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveOrganizationResources } from '../../../../hooks/organization/useRetrieveOrganizationResources';
import { useRetrieveOrganizationUsers } from '../../../../hooks/organization/useRetrieveOrganizationUsers';
import { useCreateProject } from '../../../../hooks/project/useCreateProject';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveUser } from '../../../../hooks/user/useRetrieveUser';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';
import { NewProjectForm, ProjectData } from './NewProjectForm';
import { NewProjectSummary } from './NewProjectSummary';
import { NewProjectSummaryContract } from './NewProjectSummaryContract';
import { NewProjectUsers } from './NewProjectUsers';
import { NewProjectVehicles, createVehicle, isVehicleValid } from './NewProjectVehicles';

type OrganizationNewProjectDialogProps = {
  onClose: (...args: any[]) => any;
  isOpen?: boolean;
};

export const OrganizationNewProjectDialog = ({ isOpen = false, onClose }: OrganizationNewProjectDialogProps) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: user } = useRetrieveUser();
  const { data: organization } = useRetrieveOrganization();
  const { data: organizationUsers } = useRetrieveOrganizationUsers();
  const { data: organizationResources } = useRetrieveOrganizationResources();
  const { run: createProject, isLoading, success: projectCreated, error } = useCreateProject();

  const [step, setStep] = useState<number>(0);
  const [projectData, setProjectData] = useState<ProjectData>({
    label: ``,
    description: ``,
    location: undefined,
    traffic: false,
  });
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [users, setUsers] = useState<string[]>([]);

  useEffect(() => {
    if (activeProject && organization) {
      setVehicles([createVehicle([], activeProject, organization.id)]);
    }
  }, [activeProject, organization]);

  const closeDialog = () => {
    setProjectData({
      label: ``,
      description: ``,
      location: undefined,
      traffic: false,
    });
    setStep(0);

    setUsers([]);

    if (activeProject && organization) {
      setVehicles([createVehicle([], activeProject, organization.id)]);
    }
    onClose();
  };

  useEffect(() => {
    if (error || projectCreated) {
      closeDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, projectCreated]);

  const nexStep = () => {
    if (step === 2) {
      createProject({
        ...projectData,
        vehicles,
        users: users.filter((userId) => userId !== user!.id),
        timeZone: moment.tz.guess(true),
      });
    } else {
      setStep(step + 1);
    }
  };

  const stepBack = () => {
    switch (step) {
      case 0:
        closeDialog();
        break;
      case 1:
        setStep(0);
        break;
      case 2:
        setStep(1);
        break;
    }
  };

  const getStepContent = () => {
    switch (step) {
      case 0:
        return <NewProjectForm newProject={projectData} changeProject={setProjectData} />;
      case 1:
        return (
          <NewProjectVehicles
            activeProject={activeProject!}
            organization={organization!}
            vehicles={vehicles}
            setVehicles={setVehicles}
          />
        );
      case 2:
        return (
          <NewProjectUsers
            loggedUser={user!}
            selectedUsers={users}
            setSelectedUsers={setUsers}
            organizationUsers={organizationUsers!}
          />
        );
      default:
        return null;
    }
  };

  if (!organization || !activeProject || !user || !organizationUsers || !organizationResources) {
    return null;
  }

  const isNextStepDisabled = () => {
    switch (step) {
      case 0:
        return !projectData.label || !projectData.location || organizationResources.exceedsProjectsHardLimit(1);
      case 1:
        return !vehicles.every(isVehicleValid) || organizationResources.exceedsVehicleHardLimit(vehicles.length);
      case 2:
        return isLoading;
      default:
        return true;
    }
  };

  const getNextButtonIntlId = () => {
    switch (step) {
      case 2:
        return `create`;
      default:
        return `next`;
    }
  };

  return (
    <RoutalDialog
      open={!!isOpen}
      onCloseDialog={closeDialog}
      showHeader={false}
      showFooter={false}
      dialogProps={{
        fullScreen: true,
        disableEscapeKeyDown: true,
      }}
      contentStyles={{
        position: `absolute`,
        top: 0,
        left: 0,
        padding: 0,
        width: `100%`,
        height: `100%`,
      }}
    >
      <Grid container direction="row" wrap="nowrap" style={{ height: `100%`, width: `100%`, position: `relative` }}>
        <div style={{ position: `absolute`, zIndex: 1, right: `20px`, top: `20px` }}>
          <CrossIcon
            style={{ fontSize: `16px`, cursor: isLoading ? `default` : `pointer`, color: `white` }}
            onClick={() => {
              if (isLoading) return;
              closeDialog();
            }}
          />
        </div>

        {step > 0 ? (
          <div style={{ position: `absolute`, zIndex: 1, left: `20px`, bottom: `20px` }}>
            <Grid
              container
              direction="row"
              onClick={() => {
                if (isLoading) return;
                stepBack();
              }}
            >
              <Grid item>
                <img
                  src={Back}
                  alt=""
                  draggable="false"
                  style={{ width: `15px`, cursor: isLoading ? `default` : `pointer` }}
                />
              </Grid>

              <HSpacer small />

              <Grid item>
                <Text intlId="back" style={{ cursor: isLoading ? `default` : `pointer` }} />
              </Grid>
            </Grid>
          </div>
        ) : null}

        <Grid
          item
          container
          direction="column"
          wrap="nowrap"
          style={{ position: `relative`, height: `100%` }}
          xs={7}
          alignItems="center"
          justifyContent="center"
        >
          <div style={{ position: `absolute`, zIndex: 1, right: `20px`, bottom: `20px` }}>
            <Button
              disabled={isNextStepDisabled()}
              style={{ width: `160px` }}
              intlId={getNextButtonIntlId()}
              onClick={nexStep}
            />
          </div>

          {getStepContent()}
        </Grid>

        <Grid item container xs={5}>
          {organizationResources.subscription.has_contract ? (
            <NewProjectSummaryContract step={step} vehicles={vehicles} resources={organizationResources} />
          ) : (
            <NewProjectSummary
              step={step}
              subscriptionPlan={organization.subscription.plan}
              billingPeriod={organization.subscription.billing_period}
              projectData={projectData}
              vehicles={vehicles}
            />
          )}
        </Grid>
      </Grid>
    </RoutalDialog>
  );
};
